import dayjs from "dayjs";
import { Input, TimePicker } from "antd";
import { FaRegCopy } from "react-icons/fa6";
import { RiDeleteBin5Line } from "react-icons/ri";
import { handleNumericDecimalInput } from "../../../../../../common/utils/numericInputHandler";
import style from "../style.module.scss";

const { RangePicker } = TimePicker;

interface SchedulesInputsProps {
  value: any[];
  rate?: boolean;
  onChange: (value: any[]) => void;
  onChanged?: () => void;
  canEmpty?: boolean;
}

export const SchedulesInputs = ({
  value,
  rate = false,
  onChange,
  onChanged,
  canEmpty = false,
}: SchedulesInputsProps) => {
  const emptyDaySchedule = (dayIndex: number) => {
    update((prev) =>
      prev.map((day, index) =>
        index !== dayIndex
          ? day
          : {
              ...day,
              times: [],
            }
      )
    );
  };
  
  const restoreDaySchedule = (dayIndex: number) => {
    update((prev) =>
      prev.map((day, index) =>
        index !== dayIndex
          ? day
          : {
              ...day,
              times: [
                {
                  startTime: null,
                  endTime: null,
                  ...(rate ? { rate: null } : {}),
                },
              ],
            }
      )
    );
  };

  const update = (updater: (prev: any[]) => any[]) => {
    const newValue = updater(value);
    onChange(newValue);
    onChanged?.();
  };

  const handleAddTime = (dayIndex: number) => {
    update((prev) =>
      prev.map((day, index) =>
        index !== dayIndex
          ? day
          : {
              ...day,
              times: [
                ...day.times,
                {
                  startTime: null,
                  endTime: null,
                  ...(rate ? { rate: null } : {}),
                },
              ],
            }
      )
    );
  };

  const handleDeleteTime = (dayIndex: number, timeIndex: number) => {
    update((prev) =>
      prev.map((day, index) =>
        index !== dayIndex
          ? day
          : {
              ...day,
              times: day.times.filter(
                (_: any, i: number) => i !== timeIndex
              ),
            }
      )
    );
  };

  const handleCopyTime = (dayIndex: number, timeIndex: number) => {
    update((prev) =>
      prev.map((day, index) => {
        if (index !== dayIndex) return day;

        const copied = {
          ...day.times[timeIndex],
        };

        delete copied["@id"];

        return {
          ...day,
          times: [
            ...day.times.slice(0, timeIndex + 1),
            copied,
            ...day.times.slice(timeIndex + 1),
          ],
        };
      })
    );
  };

  const handleChangeTime = (
    dayIndex: number,
    timeIndex: number,
    values: any
  ) => {
    update((prev) =>
      prev.map((day, dIndex) => {
        if (dIndex !== dayIndex) return day;

        return {
          ...day,
          times: day.times.map((time: any, tIndex: number) => {
            if (tIndex !== timeIndex) return time;

            return {
              ...time,
              startTime: values?.[0]
                ? dayjs(values[0]).format("HH:mm")
                : null,
              endTime: values?.[1]
                ? dayjs(values[1]).format("HH:mm")
                : null,
            };
          }),
        };
      })
    );
  };

  return (
    <>
      {value.map((workScheduleDay: any, dayIndex: number) => {
        const isUndefined = workScheduleDay.times.length === 0;
  
        return (
          <div
            key={workScheduleDay.day.id}
            className={style.schedulesInputs__day}
          >
            <div className={style.schedulesInputs__dayTitle}>
              {workScheduleDay.day.name}
  
              {canEmpty && (
                isUndefined ? (
                  <button
                    type="button"
                    title="Ajouter des horaires"
                    className={style.schedulesInputs__action}
                    onClick={() => restoreDaySchedule(dayIndex)}
                  >
                    +
                  </button>
                ) : (
                  <div
                    className={style.schedulesInputs__action_empty}
                    onClick={() => emptyDaySchedule(dayIndex)}
                  >
                    <RiDeleteBin5Line title="Vider les horaires" size={20} />
                  </div>
                )
              )}
            </div>
  
            {isUndefined ? (
              <div className={style.schedulesInputs__undefined}>
                Aucun horaire défini
              </div>
            ) : (
              workScheduleDay.times.map((time: any, timeIndex: number) => (
                <div
                  key={time["@id"] ?? `${dayIndex}-${timeIndex}`}
                  className={style.schedulesInputs__timeRow}
                >
                  <RangePicker
                    needConfirm={false}
                    allowClear={false}
                    format="HH:mm"
                    value={[
                      time.startTime
                        ? dayjs(time.startTime, "HH:mm")
                        : null,
                      time.endTime
                        ? dayjs(time.endTime, "HH:mm")
                        : null,
                    ]}
                    onChange={(value) =>
                      handleChangeTime(dayIndex, timeIndex, value)
                    }
                  />
  
                  {rate && (
                    <div className={style.schedulesInputs__rate}>
                      <Input
                        placeholder="Taux"
                        value={time?.rate ?? ""}
                        suffix="%"
                        onKeyDown={handleNumericDecimalInput}
                        onChange={(e) =>
                          update((prev) =>
                            prev.map((day, dIndex) => {
                              if (dIndex !== dayIndex) return day;
  
                              return {
                                ...day,
                                times: day.times.map(
                                  (t: any, tIndex: number) =>
                                    tIndex !== timeIndex
                                      ? t
                                      : {
                                          ...t,
                                          rate:
                                            e.target.value === ""
                                              ? ""
                                              : Number(e.target.value),
                                        }
                                ),
                              };
                            })
                          )
                        }
                      />
                    </div>
                  )}
  
                  <div className={style.schedulesInputs__actions}>
                    {timeIndex === 0 ? (
                      <button
                        type="button"
                        className={style.schedulesInputs__action}
                        onClick={() => handleAddTime(dayIndex)}
                      >
                        +
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={style.schedulesInputs__action}
                        onClick={() =>
                          handleDeleteTime(dayIndex, timeIndex)
                        }
                      >
                        <RiDeleteBin5Line />
                      </button>
                    )}
  
                    <button
                      type="button"
                      className={style.schedulesInputs__action}
                      onClick={() =>
                        handleCopyTime(dayIndex, timeIndex)
                      }
                    >
                      <FaRegCopy />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        );
      })}
    </>
  );
};